import type {AuthBootstrap,SurfaceIdentity} from './model';

export type Claims=Record<string,unknown>&{
  iss?:string;sub?:string;aud?:string|string[];exp?:number;nonce?:string;
};

export type AuthSession = {
  status:'loading'|'signed-out'|'signed-in'|'error';
  message:string;
  reason?:'tenant-unavailable'|'permission-refused'|'session-refused';
  bootstrap?:AuthBootstrap;
  claims?:Claims;
  token?:string;
};

class AuthRefusal extends Error{
  constructor(readonly reason:'tenant-unavailable'|'permission-refused',message:string){super(message);}
}

const SESSION_KEYS=[
  'tallyup.access-token.v1','tallyup.pkce-verifier.v1','tallyup.oauth-state.v1',
  'tallyup.oauth-nonce.v1','tallyup.return-path.v1'
] as const;

const audienceIncludes=(claim:string|string[]|undefined,expected:string)=>
  typeof claim==='string'?claim===expected:Array.isArray(claim)&&claim.includes(expected);

const randomBase64Url=(bytes:number)=>{
  const value=crypto.getRandomValues(new Uint8Array(bytes));
  return btoa(String.fromCharCode(...value)).replaceAll('+','-').replaceAll('/','_').replaceAll('=','');
};

const sha256Base64Url=async(value:string)=>{
  const digest=await crypto.subtle.digest('SHA-256',new TextEncoder().encode(value));
  return btoa(String.fromCharCode(...new Uint8Array(digest))).replaceAll('+','-').replaceAll('/','_').replaceAll('=','');
};

const record=(value:unknown):value is Record<string,unknown>=>
  typeof value==='object'&&value!==null&&!Array.isArray(value);
const utf8Decoder=new TextDecoder('utf-8',{fatal:true});

const base64UrlBytes=(value:string,label:string)=>{
  if(!value||!/^[A-Za-z0-9_-]+$/.test(value)) throw new Error(`${label} encoding is invalid.`);
  const encoded=value.replaceAll('-','+').replaceAll('_','/');
  try{
    return Uint8Array.from(atob(encoded.padEnd(Math.ceil(encoded.length/4)*4,'=')),character=>character.charCodeAt(0));
  }catch{
    throw new Error(`${label} encoding is invalid.`);
  }
};

const jsonSegment=(value:string,label:string)=>{
  try{
    const parsed=JSON.parse(utf8Decoder.decode(base64UrlBytes(value,label)));
    if(!record(parsed)) throw new Error();
    return parsed;
  }catch(error){
    if(error instanceof Error&&error.message===`${label} encoding is invalid.`) throw error;
    throw new Error(`${label} JSON is invalid.`);
  }
};

const selectedJwk=async(endpoint:string,kid:string,algorithm:string)=>{
  const response=await fetch(endpoint,{cache:'no-store',credentials:'omit',redirect:'error'});
  if(!response.ok) throw new Error('Selected signing keys are unavailable.');
  const body=await response.json() as unknown;
  if(!record(body)||!Array.isArray(body.keys)) throw new Error('Selected signing keys are invalid.');
  const matches=body.keys.filter((candidate):candidate is JsonWebKey&Record<string,unknown>=>
    record(candidate)&&candidate.kty==='RSA'&&candidate.kid===kid
      &&typeof candidate.n==='string'&&typeof candidate.e==='string'
      &&(candidate.alg===undefined||candidate.alg===algorithm)
      &&(candidate.use===undefined||candidate.use==='sig')
      &&(candidate.key_ops===undefined||(Array.isArray(candidate.key_ops)&&candidate.key_ops.includes('verify')))
      &&candidate.d===undefined);
  if(matches.length!==1) throw new Error('Token signing key is not uniquely selected.');
  return matches[0];
};

const verifiedJwtClaims=async(token:string,bootstrap:AuthBootstrap):Promise<Claims>=>{
  const segments=token.split('.');
  if(segments.length!==3) throw new Error('Token shape is invalid.');
  const header=jsonSegment(segments[0],'Token header');
  if(header.alg!==bootstrap.signatureAlgorithm||header.alg!=='RS256')
    throw new Error('Token signature algorithm refused.');
  if(typeof header.kid!=='string'||header.kid.length===0||header.crit!==undefined
    ||header.jku!==undefined||header.jwk!==undefined||header.x5u!==undefined)
    throw new Error('Token signing key selection refused.');
  const key=await selectedJwk(bootstrap.jwksEndpoint,header.kid,bootstrap.signatureAlgorithm);
  let verificationKey:CryptoKey;
  try{
    verificationKey=await crypto.subtle.importKey('jwk',key,
      {name:'RSASSA-PKCS1-v1_5',hash:'SHA-256'},false,['verify']);
  }catch{
    throw new Error('Selected signing key is invalid.');
  }
  const valid=await crypto.subtle.verify('RSASSA-PKCS1-v1_5',verificationKey,
    base64UrlBytes(segments[2],'Token signature'),new TextEncoder().encode(`${segments[0]}.${segments[1]}`));
  if(!valid) throw new Error('Token signature refused.');
  return jsonSegment(segments[1],'Token claims') as Claims;
};

function validateClaims(claims:Claims,bootstrap:AuthBootstrap){
  const now=Math.floor(Date.now()/1000);
  if(claims.iss!==bootstrap.issuer||!audienceIncludes(claims.aud,bootstrap.audience)
    ||typeof claims.exp!=='number'||claims.exp<=now||typeof claims[bootstrap.principalClaim]!=='string')
    throw new Error('Session identity does not match this application.');
  const tenant=claims[bootstrap.tenantClaim];
  if(typeof tenant!=='string'||!/^ten_[A-Za-z0-9_-]{6,64}$/.test(tenant))
    throw new AuthRefusal('tenant-unavailable','Session has no admitted tenant.');
  const permissions=claims[bootstrap.permissionsClaim];
  const selected=Array.isArray(permissions)?permissions.filter(value=>typeof value==='string'):[];
  if(bootstrap.requiredPermissions.some(permission=>!selected.includes(permission)))
    throw new AuthRefusal('permission-refused','Session lacks required permissions.');
}

async function loadBootstrap(source:string,surface:SurfaceIdentity){
  if(!source.startsWith('/')) throw new Error('Authentication bootstrap location is invalid.');
  const response=await fetch(source,{cache:'no-store'});
  if(!response.ok) throw new Error('Authentication bootstrap unavailable.');
  const bootstrap=await response.json() as AuthBootstrap;
  const production=bootstrap.schema==='tallyup.auth-bootstrap.v3';
  const synthetic=bootstrap.schema==='tallyup.auth-bootstrap.v4'&&'profile' in bootstrap&&bootstrap.profile==='synthetic-demo';
  const selectedOrigin=location.origin;
  const loopback=selectedOrigin.startsWith('http://127.0.0.1:')||selectedOrigin.startsWith('http://localhost:');
  const demoUrl=(value:unknown)=>typeof value==='string'&&new URL(value,selectedOrigin).origin===selectedOrigin
    &&(selectedOrigin.startsWith('https://')||loopback);
  if((!production&&!synthetic)||bootstrap.surface!==surface
    ||typeof bootstrap.issuer!=='string'||(production?!bootstrap.issuer.startsWith('https://'):!demoUrl(bootstrap.issuer))
    ||typeof bootstrap.jwksEndpoint!=='string'||(production?!bootstrap.jwksEndpoint.startsWith('https://'):!demoUrl(bootstrap.jwksEndpoint))
    ||bootstrap.signatureAlgorithm!=='RS256'
    ||typeof bootstrap.audience!=='string'||bootstrap.audience.length===0
    ||typeof bootstrap.clientId!=='string'||bootstrap.clientId.length===0
    ||typeof bootstrap.redirectUri!=='string'||(production?!bootstrap.redirectUri.startsWith('https://'):!demoUrl(bootstrap.redirectUri))
    ||typeof bootstrap.tenantClaim!=='string'||bootstrap.tenantClaim.length===0
    ||typeof bootstrap.principalClaim!=='string'||bootstrap.principalClaim.length===0
    ||typeof bootstrap.permissionsClaim!=='string'||bootstrap.permissionsClaim.length===0
    ||!Array.isArray(bootstrap.requiredPermissions)
    ||bootstrap.requiredPermissions.some(value=>typeof value!=='string'||value.length===0)
    ||(synthetic&&(bootstrap.surface!=='tenant'||!demoUrl(bootstrap.sessionEndpoint)
      ||bootstrap.requiredPermissions.length!==0||bootstrap.environment?.kind!=='synthetic-demo'
      ||bootstrap.environment.resettable!==true||bootstrap.environment.providerEffects!=='refused')))
    throw new Error('Authentication bootstrap refused.');
  return bootstrap;
}

async function provisionSyntheticSession(bootstrap:AuthBootstrap){
  if(bootstrap.schema!=='tallyup.auth-bootstrap.v4')return null;
  const response=await fetch(bootstrap.sessionEndpoint,{method:'GET',cache:'no-store',credentials:'omit',redirect:'error',headers:{accept:'application/json'}});
  if(!response.ok)throw new Error('Synthetic demo session unavailable.');
  const body=await response.json() as unknown;
  if(!record(body)||body.schema!=='tallyup.synthetic-demo-session.v1'||typeof body.accessToken!=='string')
    throw new Error('Synthetic demo session refused.');
  sessionStorage.setItem('tallyup.access-token.v1',body.accessToken);
  return body.accessToken;
}

async function loadDiscovery(bootstrap:AuthBootstrap){
  const response=await fetch(new URL('.well-known/openid-configuration',bootstrap.issuer),{redirect:'error'});
  if(!response.ok) throw new Error('Identity discovery unavailable.');
  const discovery=await response.json() as unknown;
  if(!record(discovery)||discovery.issuer!==bootstrap.issuer
    ||discovery.jwks_uri!==bootstrap.jwksEndpoint)
    throw new Error('Identity discovery refused.');
  return discovery;
}

async function finishLogin(bootstrap:AuthBootstrap,defaultRoute:string){
  const query=new URLSearchParams(location.search);
  const code=query.get('code'),oauthState=query.get('state');
  const expectedState=sessionStorage.getItem('tallyup.oauth-state.v1');
  const verifier=sessionStorage.getItem('tallyup.pkce-verifier.v1');
  const expectedNonce=sessionStorage.getItem('tallyup.oauth-nonce.v1');
  if(!code||!oauthState||oauthState!==expectedState||!verifier||!expectedNonce)
    throw new Error('Authentication callback state refused.');
  const discovery=await loadDiscovery(bootstrap);
  if(typeof discovery.token_endpoint!=='string') throw new Error('Identity token endpoint unavailable.');
  const response=await fetch(discovery.token_endpoint,{method:'POST',headers:{'content-type':'application/x-www-form-urlencoded'},body:new URLSearchParams({
    grant_type:'authorization_code',client_id:bootstrap.clientId,code,code_verifier:verifier,redirect_uri:bootstrap.redirectUri
  })});
  if(!response.ok) throw new Error('Authentication code exchange refused.');
  const tokens=await response.json();
  if(typeof tokens.access_token!=='string'||typeof tokens.id_token!=='string')
    throw new Error('Authentication response is incomplete.');
  const identity=await verifiedJwtClaims(tokens.id_token,bootstrap);
  if(identity.iss!==bootstrap.issuer||!audienceIncludes(identity.aud,bootstrap.clientId)
    ||identity.nonce!==expectedNonce||typeof identity.exp!=='number'||identity.exp<=Math.floor(Date.now()/1000))
    throw new Error('Identity token claims refused.');
  sessionStorage.setItem('tallyup.access-token.v1',tokens.access_token);
  for(const key of ['tallyup.pkce-verifier.v1','tallyup.oauth-state.v1','tallyup.oauth-nonce.v1'])
    sessionStorage.removeItem(key);
  const returnPath=sessionStorage.getItem('tallyup.return-path.v1')||defaultRoute;
  history.replaceState(null,'',returnPath);
}

export async function initializeSession(surface:SurfaceIdentity,source:string,defaultRoute:string):Promise<AuthSession>{
  let bootstrap:AuthBootstrap|undefined;
  try{
    bootstrap=await loadBootstrap(source,surface);
    if(new URLSearchParams(location.search).has('code')) await finishLogin(bootstrap,defaultRoute);
    const token=sessionStorage.getItem('tallyup.access-token.v1')||await provisionSyntheticSession(bootstrap);
    if(!token) return {status:'signed-out',message:'',bootstrap};
    const claims=await verifiedJwtClaims(token,bootstrap);
    validateClaims(claims,bootstrap);
    return {status:'signed-in',message:'',bootstrap,claims,token};
  }catch(error){
    sessionStorage.removeItem('tallyup.access-token.v1');
    return {status:'error',message:error instanceof Error?error.message:'Session refused.',bootstrap,
      reason:error instanceof AuthRefusal?error.reason:'session-refused'};
  }
}

export async function startLogin(bootstrap:AuthBootstrap,returnPath:string){
  if(bootstrap.schema==='tallyup.auth-bootstrap.v4')throw new Error('Synthetic demo sessions do not invoke an identity provider.');
  const discovery=await loadDiscovery(bootstrap);
  if(typeof discovery.authorization_endpoint!=='string') throw new Error('Identity discovery refused.');
  const verifier=randomBase64Url(48),oauthState=randomBase64Url(24),nonce=randomBase64Url(24);
  sessionStorage.setItem('tallyup.pkce-verifier.v1',verifier);
  sessionStorage.setItem('tallyup.oauth-state.v1',oauthState);
  sessionStorage.setItem('tallyup.oauth-nonce.v1',nonce);
  sessionStorage.setItem('tallyup.return-path.v1',returnPath);
  const authorize=new URL(discovery.authorization_endpoint);
  authorize.search=new URLSearchParams({
    response_type:'code',client_id:bootstrap.clientId,redirect_uri:bootstrap.redirectUri,
    audience:bootstrap.audience,scope:'openid profile email',code_challenge:await sha256Base64Url(verifier),
    code_challenge_method:'S256',state:oauthState,nonce
  }).toString();
  location.assign(authorize);
}

export function clearSession(){
  for(const key of SESSION_KEYS) sessionStorage.removeItem(key);
}
